import { Dialog, Typography } from '@mui/material';
import { DashboardCard, ProductCard } from '../../utils/dashboard/dashboardCards';
/* eslint-disable no-underscore-dangle */
import type { Store, UniqueArticle } from '@gozoki/api-types';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useInventory } from '@gozoki/api';
import { frenchFormat } from '@gozoki/tools';
import { DateTime } from 'luxon';
import { useToast } from '../../components/communication/Toast';
import { prepareReceptionData } from '../../utils/excel/prepareReceptionData';
import { writeReceptionFile } from '../../utils/excel/writeReceptionXlsx';
import { writeArticlesDetailsFile } from '../../utils/excel/writeStoresFiles';
import { aggregateProducts } from '../../utils/inventory/aggregateProducts';
import { generateReceptionPdf } from '../../utils/pdf/writeReceptionPdf';
import { fonts } from '../../utils/theme/fonts.theme';
import type { ProductWithCount } from '../dashboard/DashboardPage';

export interface InventoryStats {
    delivered: UniqueArticle[];
    destroyed: UniqueArticle[];
    stolen: UniqueArticle[];
    errored: UniqueArticle[];
    sold: UniqueArticle[];
    defective: UniqueArticle[];
}

export interface InventoryStatsDetailLineProps {
    inventoryId: number;
    setInventoryStats: (inventoryId: number, inventoryStats: InventoryStats) => void;
    globalDatas: Record<string, InventoryStats>;
    store: Store | undefined | null;
}

type ProductsMode =
    | 'before'
    | 'flashed'
    | 'delivered'
    | 'destroyed'
    | 'defective'
    | 'stolen'
    | 'sold'
    | 'error';

const DETAILS_LABELS: Record<ProductsMode, string> = {
    before: 'Détails des produits avant inventaire',
    flashed: 'Détails des produits flashés',
    delivered: 'Détails des produits livrés',
    destroyed: 'Détails des produits cassés',
    defective: 'Détails des produits défectueux',
    stolen: 'Détails des produits volés',
    sold: 'Détails des produits vendus',
    error: 'Détails des produits en erreur (réapparus entre temps)',
};

export const InventoryStatsDetailLine = ({
    inventoryId,
    setInventoryStats,
    globalDatas,
    store,
}: InventoryStatsDetailLineProps) => {
    const { data: inventory, isLoading: isLoadingInventory } = useInventory(inventoryId);
    const [Toast, showToast] = useToast();

    const frenchLabel = useMemo(() => {
        if (!inventory) return '';
        const inventoryDate = new Date(inventory?.createdAt);
        return `Inventaire du ${inventoryDate.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: 'long',
            year: 'numeric',
        })}`;
    }, [inventory]);

    const beforeInventoryArticles = useMemo(() => {
        if (!inventory) return [];
        return inventory?.currentStock;
    }, [inventory]);

    const totalArticles = useMemo(() => {
        if (!inventory) return [];
        return inventory?.incomingStock;
    }, [inventory]);

    const deliveredArticles = useMemo(() => {
        if (!inventory) return [];
        return inventory?.deliveredArticles;
    }, [inventory]);

    const { defectiveArticles, destroyedArticles, soldArticles, errorArticles, stolenArticles } =
        useMemo(() => {
            if (!inventory) {
                return {
                    defectiveArticles: [],
                    destroyedArticles: [],
                    soldArticles: [],
                    errorArticles: [],
                    stolenArticles: [],
                };
            }
            const inventoryDate = new Date(inventory.createdAt);
            const _defectiveArticles: Map<string, UniqueArticle> = new Map();
            const _destroyedArticles: Map<string, UniqueArticle> = new Map();
            const _soldArticles: Map<string, UniqueArticle> = new Map();
            const _errorArticles: Map<string, UniqueArticle> = new Map();
            const _stolenArticles: Map<string, UniqueArticle> = new Map();
            inventory.lostArticles.forEach((article) => {
                const articleDlc = new Date(article.dlc);
                if (['DELETED'].includes(article.state)) {
                    // this cant count, it is soft deleted
                } else if (['SOLD'].includes(article.state)) {
                    // easiest : it is sold
                    _soldArticles.set(article.checkpointArticleRef, article);
                } else if (['DEFECTIVE_PACKAGING', 'FAULTY'].includes(article.state)) {
                    _defectiveArticles.set(article.checkpointArticleRef, article);
                } else if (article.state === 'DESTROYED' || articleDlc < inventoryDate) {
                    _destroyedArticles.set(article.checkpointArticleRef, article);
                } else if (['IN_STOCK'].includes(article.state)) {
                    _errorArticles.set(article.checkpointArticleRef, article);
                } else {
                    // this is a stolen article
                    _stolenArticles.set(article.checkpointArticleRef, article);
                }
            });

            return {
                defectiveArticles: Array.from(_defectiveArticles.values()),
                destroyedArticles: Array.from(_destroyedArticles.values()),
                soldArticles: Array.from(_soldArticles.values()),
                errorArticles: Array.from(_errorArticles.values()),
                stolenArticles: Array.from(_stolenArticles.values()),
            };
        }, [inventory]);

    // -------------------- details --------------------//

    const [productsToShow, setProductsToShow] = useState<ProductsMode>('flashed');
    const [showDetailsProducts, setShowDetailsProducts] = useState(false);
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

    const articlesToShow = useMemo(() => {
        if (productsToShow === 'before') return beforeInventoryArticles;
        if (productsToShow === 'flashed') return totalArticles;
        if (productsToShow === 'delivered') return deliveredArticles;
        if (productsToShow === 'destroyed') return destroyedArticles;
        if (productsToShow === 'defective') return defectiveArticles;
        if (productsToShow === 'stolen') return stolenArticles;
        if (productsToShow === 'error') return errorArticles;
        if (productsToShow === 'sold') return soldArticles;
        return [];
    }, [
        beforeInventoryArticles,
        deliveredArticles,
        destroyedArticles,
        errorArticles,
        defectiveArticles,
        productsToShow,
        stolenArticles,
        totalArticles,
        soldArticles,
    ]);

    const productsDataToShow: ProductWithCount[] = useMemo(() => {
        return aggregateProducts(articlesToShow, 'price');
    }, [articlesToShow]);

    const productsToShowCount = useMemo(() => {
        return productsDataToShow.reduce((acc, product) => acc + product.count, 0);
    }, [productsDataToShow]);

    const productsToShowCa = useMemo(() => {
        return productsDataToShow.reduce((acc, product) => acc + product.ca, 0);
    }, [productsDataToShow]);

    const detailsLabel = useMemo(() => {
        return DETAILS_LABELS[productsToShow] ?? 'Détails des produits';
    }, [productsToShow]);

    const buildFileLabel = useCallback(
        (mode: ProductsMode) => {
            const productDetails = (DETAILS_LABELS[mode] ?? 'Détails des produits')
                .toLowerCase()
                .replace(/\s+/g, '_');
            const dateDetails = frenchLabel
                ? frenchLabel.toLowerCase().replace(/\s+/g, '_')
                : 'inventaire';
            return `${dateDetails}_${productDetails}.xlsx`;
        },
        [frenchLabel]
    );

    const getArticlesForMode = useCallback(
        (mode: ProductsMode) => {
            switch (mode) {
                case 'before':
                    return beforeInventoryArticles;
                case 'flashed':
                    return totalArticles;
                case 'delivered':
                    return deliveredArticles;
                case 'destroyed':
                    return destroyedArticles;
                case 'defective':
                    return defectiveArticles;
                case 'stolen':
                    return stolenArticles;
                case 'sold':
                    return soldArticles;
                case 'error':
                    return errorArticles;
                default:
                    return [];
            }
        },
        [
            beforeInventoryArticles,
            totalArticles,
            deliveredArticles,
            destroyedArticles,
            defectiveArticles,
            stolenArticles,
            soldArticles,
            errorArticles,
        ]
    );

    const handleDownloadPdf = useCallback(async () => {
        if (!store || !inventory) return;
        setIsGeneratingPdf(true);
        try {
            const deliveredProductsData = aggregateProducts(deliveredArticles, 'price');

            await generateReceptionPdf(
                deliveredProductsData,
                store,
                DateTime.fromISO(inventory.createdAt)
            );
        } catch (error) {
            console.error('Error generating PDF:', error);
            showToast({
                message: 'Erreur lors de la génération du PDF. Veuillez réessayer.',
                severity: 'error',
            });
        } finally {
            setIsGeneratingPdf(false);
        }
    }, [deliveredArticles, store, inventory, showToast]);

    const handleDownloadCsv = useCallback(
        async (mode: ProductsMode) => {
            if (mode === 'delivered') {
                if (!store || !inventory) return;
                setIsGeneratingPdf(true);
                try {
                    const receptionDate = DateTime.fromISO(inventory.createdAt);
                    const deliveredProductsData = aggregateProducts(deliveredArticles, 'price');
                    const productsWithCountAndNeeded = await prepareReceptionData(
                        deliveredProductsData,
                        store,
                        receptionDate
                    );

                    await writeReceptionFile(
                        productsWithCountAndNeeded,
                        store.label,
                        receptionDate.toFormat('yyyy-MM-dd'),
                        store.label
                    );
                } catch (error) {
                    console.error('Error generating CSV:', error);
                    showToast({
                        message: 'Erreur lors de la génération du CSV. Veuillez réessayer.',
                        severity: 'error',
                    });
                } finally {
                    setIsGeneratingPdf(false);
                }
                return;
            }

            const articles = getArticlesForMode(mode);
            const products = aggregateProducts(articles, 'price');
            const fileLabel = buildFileLabel(mode);
            writeArticlesDetailsFile(products, fileLabel, store?.label);
        },
        [buildFileLabel, getArticlesForMode, deliveredArticles, inventory, showToast, store]
    );

    // -------------------- global datas --------------------//

    const inventoryStats: InventoryStats = useMemo(() => {
        return {
            delivered: deliveredArticles,
            defective: defectiveArticles,
            destroyed: destroyedArticles,
            errored: errorArticles,
            stolen: stolenArticles,
            sold: soldArticles,
        };
    }, [
        deliveredArticles,
        defectiveArticles,
        destroyedArticles,
        errorArticles,
        stolenArticles,
        soldArticles,
    ]);

    // Debounce pour éviter les mises à jour trop fréquentes
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            // Refresh if no globalDatas entry for this inventory,
            // or if any inventoryStats array differs by length, or by shallow comparison for contents.
            const prevStats = globalDatas[inventoryId];
            const arraysAreDifferent = (a: UniqueArticle[], b: UniqueArticle[]) =>
                a.length !== b.length || a.some((item, idx) => item !== b[idx]);
            if (
                !prevStats ||
                arraysAreDifferent(prevStats.defective, inventoryStats.defective) ||
                arraysAreDifferent(prevStats.destroyed, inventoryStats.destroyed) ||
                arraysAreDifferent(prevStats.stolen, inventoryStats.stolen) ||
                arraysAreDifferent(prevStats.errored, inventoryStats.errored) ||
                arraysAreDifferent(prevStats.sold, inventoryStats.sold) ||
                arraysAreDifferent(prevStats.delivered, inventoryStats.delivered)
            ) {
                setInventoryStats(inventoryId, inventoryStats);
            }
        }, 100); // 100ms de debounce

        return () => clearTimeout(timeoutId);
    }, [inventoryId, inventoryStats, setInventoryStats, globalDatas]);

    const soldArticlesCa = useMemo(() => {
        return soldArticles.reduce((acc, article) => acc + (article.soldPrice ?? 0), 0);
    }, [soldArticles]);

    const destroyedArticlesPercentage = useMemo(() => {
        const destroyedArticlesCa = destroyedArticles.reduce(
            (acc, article) => acc + (article.product.price ?? 0),
            0
        );
        const ratio = destroyedArticlesCa / soldArticlesCa;
        const percentage = ratio * 100;
        return percentage > 0 ? `${percentage.toFixed(2)}%` : undefined;
    }, [destroyedArticles, soldArticlesCa]);

    const defectiveArticlesPercentage = useMemo(() => {
        const defectiveArticlesCa = defectiveArticles.reduce(
            (acc, article) => acc + (article.product.price ?? 0),
            0
        );
        const ratio = defectiveArticlesCa / soldArticlesCa;
        const percentage = ratio * 100;
        return percentage > 0 ? `${percentage.toFixed(2)}%` : undefined;
    }, [defectiveArticles, soldArticlesCa]);

    const stolenArticlesPercentage = useMemo(() => {
        const stolenArticlesCa = stolenArticles.reduce(
            (acc, article) => acc + (article.product.price ?? 0),
            0
        );
        const ratio = stolenArticlesCa / soldArticlesCa;
        const percentage = ratio * 100;
        return percentage > 0 ? `${percentage.toFixed(2)}%` : undefined;
    }, [stolenArticles, soldArticlesCa]);

    return (
        <div style={fonts.roboto}>
            <Toast />
            <Dialog
                sx={fonts.roboto}
                open={showDetailsProducts}
                onClose={() => setShowDetailsProducts(false)}
            >
                <div
                    style={{
                        minWidth: 500,
                        minHeight: 100,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    <Typography
                        style={fonts.pageSubtitle}
                        sx={{ textAlign: 'center', marginBottom: 2, marginTop: 2 }}
                    >
                        {detailsLabel}
                    </Typography>

                    <div style={{ maxHeight: 950, overflow: 'auto' }}>
                        {productsDataToShow?.map((product) => (
                            <ProductCard
                                key={product.id}
                                product={product}
                                count={product.count}
                                ca={product.ca}
                                mode="count"
                                maxCount={productsDataToShow[0].count}
                                maxCa={productsDataToShow[0].ca}
                                totalCount={productsToShowCount}
                                totalCa={productsToShowCa}
                            />
                        ))}
                    </div>
                </div>
            </Dialog>
            <Typography
                sx={{ paddingLeft: 2, paddingTop: 1 }}
                style={fonts.informationSubText}
                flexGrow={1}
            >
                {frenchLabel}
            </Typography>
            <div style={{ display: 'flex', flexDirection: 'row', paddingBottom: 20 }}>
                <DashboardCard
                    label="Produits vendus"
                    bigValue={`${frenchFormat(soldArticles.length)}`}
                    details={() => {
                        setProductsToShow('sold');
                        setShowDetailsProducts(true);
                    }}
                    downloadCsv={() => {
                        setProductsToShow('sold');
                        handleDownloadCsv('sold');
                    }}
                    isLoading={isLoadingInventory}
                />
                <DashboardCard
                    label="Produits en stock inventaire"
                    bigValue={`${frenchFormat(totalArticles.length)}`}
                    details={() => {
                        setProductsToShow('flashed');
                        setShowDetailsProducts(true);
                    }}
                    downloadCsv={() => {
                        setProductsToShow('flashed');
                        handleDownloadCsv('flashed');
                    }}
                    isLoading={isLoadingInventory}
                />
                <DashboardCard
                    label="Produits livrés"
                    bigValue={`${frenchFormat(deliveredArticles.length)}`}
                    details={() => {
                        setProductsToShow('delivered');
                        setShowDetailsProducts(true);
                    }}
                    downloadPdf={() => {
                        setProductsToShow('delivered');
                        handleDownloadPdf();
                    }}
                    downloadCsv={() => {
                        setProductsToShow('delivered');
                        handleDownloadCsv('delivered');
                    }}
                    isDownloading={isGeneratingPdf}
                    isLoading={isLoadingInventory}
                />
                <DashboardCard
                    label="Produits cassés"
                    bigValue={destroyedArticlesPercentage ?? '0%'}
                    details={() => {
                        setProductsToShow('destroyed');
                        setShowDetailsProducts(true);
                    }}
                    downloadCsv={() => {
                        setProductsToShow('destroyed');
                        handleDownloadCsv('destroyed');
                    }}
                    smallValue={`${frenchFormat(destroyedArticles.length)} UVC`}
                    smallOrange={destroyedArticles.length > 0}
                    isLoading={isLoadingInventory}
                />
                <DashboardCard
                    label="Produits volés (ou perdus)"
                    bigValue={stolenArticlesPercentage ?? '0%'}
                    details={() => {
                        setProductsToShow('stolen');
                        setShowDetailsProducts(true);
                    }}
                    downloadCsv={() => {
                        setProductsToShow('stolen');
                        handleDownloadCsv('stolen');
                    }}
                    smallValue={`${frenchFormat(stolenArticles.length)} UVC`}
                    smallOrange={stolenArticles.length > 0}
                    isLoading={isLoadingInventory}
                />
                <DashboardCard
                    label="Produits défectueux"
                    bigValue={defectiveArticlesPercentage ?? '0%'}
                    details={() => {
                        setProductsToShow('defective');
                        setShowDetailsProducts(true);
                    }}
                    downloadCsv={() => {
                        setProductsToShow('defective');
                        handleDownloadCsv('defective');
                    }}
                    smallValue={`${frenchFormat(defectiveArticles.length)} UVC`}
                    smallOrange={defectiveArticles.length > 0}
                    isLoading={isLoadingInventory}
                />
                <DashboardCard
                    label="Produits en erreur (réapparus entre temps)"
                    bigValue={`${frenchFormat(errorArticles.length)}`}
                    details={() => {
                        setProductsToShow('error');
                        setShowDetailsProducts(true);
                    }}
                    downloadCsv={() => {
                        setProductsToShow('error');
                        handleDownloadCsv('error');
                    }}
                    isLoading={isLoadingInventory}
                />
            </div>
        </div>
    );
};
